import { styled } from '@mui/material/styles';
import { Typography, Button, Box, IconButton } from '@mui/material';

// Colors for light theme
export const colors = {
  primary: '#0C8466',
  secondary: '#DA9959',
  tertiary: '#2D284E',
  resonance: '#c2410c',
  dark: '#0A0A0A',
  darkAlt: '#111111',
  background: '#FFFFFF',
  backgroundGradient: 'linear-gradient(to bottom, #F8FAFB, #EEF2F6)',
  text: '#1A1A1A',
  textLight: '#4A4A4A',
  textOnDark: '#E8E8E8',
  textOnDarkMuted: 'rgba(255,255,255,0.65)',
  cardBackground: 'rgba(255, 255, 255, 0.9)',
  cardBorder: 'rgba(0, 0, 0, 0.1)',
  pillar: {
    resonance: '#c2410c',
    biochar: '#0C8466',
    advisory: '#2D284E',
  },
  sectionBg: {
    agritech: 'rgba(12, 132, 102, 0.03)',
    carbon: 'rgba(218, 153, 89, 0.03)',
    consulting: 'rgba(45, 40, 78, 0.03)',
    climate: 'rgba(43, 122, 158, 0.03)',
    corporate: 'rgba(43, 122, 158, 0.03)',
  }
};

// Business line backgrounds
export const businessBackgrounds = {
  agritech: "https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  carbon: "https://images.unsplash.com/photo-1511497584788-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  consulting: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  climate: "https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
  corporate: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
};

// Business line gradients
export const businessGradients = {
  agritech: 'linear-gradient(135deg, rgba(12, 132, 102, 0.95), rgba(12, 132, 102, 0.7))',
  carbon: 'linear-gradient(135deg, rgba(218, 153, 89, 0.95), rgba(218, 153, 89, 0.7))',
  consulting: 'linear-gradient(135deg, rgba(45, 40, 78, 0.95), rgba(45, 40, 78, 0.7))',
  climate: 'linear-gradient(135deg, rgba(43, 122, 158, 0.95), rgba(43, 122, 158, 0.7))',
  corporate: 'linear-gradient(135deg, rgba(43, 122, 158, 0.95), rgba(43, 122, 158, 0.7))'
};

// Section backgrounds - more subtle, professional images
export const sectionBackgrounds = {
  agritech: "https://images.unsplash.com/photo-1587049633312-d628ae50a8ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  carbon: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  consulting: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  climate: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  corporate: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
};

// Base Styled Components
export const PageWrapper = styled('div')({
  position: 'relative',
  width: '100vw',
  margin: 0,
  padding: 0,
  background: colors.background,
  color: colors.text,
});

export const Section = styled(Box)({
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  padding: '120px 0',
  background: colors.background,
});

export const HeroSection = styled(Box)({
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  backgroundImage: 'url("https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,1) 20%, rgba(255,255,255,0.8) 100%)',
    zIndex: 0,
  },
});

export const DeepDiveSection = styled(Box)(({ theme, variant }) => ({
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  overflow: 'visible',
  padding: '160px 0',
  backgroundColor: variant ? colors.sectionBg[variant] : colors.background,
  backgroundImage: variant ? `url(${sectionBackgrounds[variant]})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'rgba(255, 255, 255, 0.97)',
    zIndex: 1,
  },
  '& > .MuiContainer-root': {
    position: 'relative',
    zIndex: 3,
  }
}));

export const ScrollPrompt = styled(IconButton)({
  position: 'absolute',
  bottom: '40px',
  left: '50%',
  transform: 'translateX(-50%)',
  color: colors.primary,
  '&:hover': {
    backgroundColor: 'rgba(12, 132, 102, 0.1)',
  },
});

export const BusinessLine = styled(Box)(({ type }) => ({
  position: 'relative',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  transform: 'skew(-8deg)',
  transformOrigin: 'center',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  borderRadius: '12px',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-5%',
    right: '-5%',
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.4)',
    transform: 'skew(8deg)',
    transition: 'all 0.5s ease',
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-5%',
    right: '-5%',
    bottom: 0,
    background: `url("${businessBackgrounds[type]}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transform: 'skew(8deg) scale(1.1)',
    filter: 'brightness(0.9)',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
  },
  '&:hover': {
    transform: 'skew(-8deg) scale(1.03)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15)',
    '&::before': {
      background: businessGradients[type],
    },
    '&::after': {
      transform: 'skew(8deg) scale(1.2)',
      filter: 'brightness(1)',
    },
    '& .content': {
      transform: 'skew(8deg) translateY(-5px)',
      background: 'transparent',
      backdropFilter: 'none',
      boxShadow: 'none',
    },
    '& .title': {
      color: 'white',
      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    '& .subtitle': {
      color: 'rgba(255,255,255,0.9)',
    },
    '& .description': {
      color: 'rgba(255,255,255,0.9)',
      maxWidth: '90%',
    }
  }
}));

export const BusinessContent = styled(Box)({
  position: 'relative',
  transform: 'skew(8deg)',
  color: colors.text,
  zIndex: 2,
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: '2.5rem',
  background: 'transparent',
  backdropFilter: 'none',
  borderRadius: '4px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

export const GradientText = styled(Typography)({
  color: colors.primary,
  fontWeight: 700,
  position: 'relative',
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: '40px',
    height: '3px',
    background: colors.primary,
    borderRadius: '2px',
  }
});

export const GlowingButton = styled(Button)({
  backgroundColor: colors.primary,
  color: 'white',
  padding: '12px 32px',
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: colors.primary,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(12, 132, 102, 0.3)',
  },
});

export const StatCard = styled('div')({
  position: 'relative',
  padding: '2rem',
  background: colors.cardBackground,
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const ImpactCircle = styled('div')({
  position: 'relative',
  width: '200px',
  height: '200px',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: colors.cardBackground,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const MetricCircle = styled('div')({
  position: 'relative',
  width: '180px',
  height: '180px',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: colors.cardBackground,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const TechShowcase = styled('div')({
  position: 'relative',
  background: colors.cardBackground,
  borderRadius: '8px',
  overflow: 'hidden',
  padding: '2rem',
  height: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const ImpactMetric = styled('div')({
  position: 'relative',
  background: colors.cardBackground,
  padding: '2rem',
  borderRadius: '8px',
  overflow: 'hidden',
  height: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const FeatureCard = styled('div')({
  position: 'relative',
  background: colors.cardBackground,
  padding: '2rem',
  height: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const CaseStudyCard = styled('div')({
  position: 'relative',
  background: colors.cardBackground,
  overflow: 'hidden',
  padding: '2rem',
  height: '100%',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${colors.cardBorder}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const TestimonialCard = styled('div')({
  position: 'relative',
  background: colors.cardBackground,
  padding: '2rem',
  borderRadius: '8px',
  borderLeft: `4px solid ${colors.primary}`,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
});

export const WaveBackground = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  opacity: 0.1,
  background: `
    radial-gradient(circle at 30% 50%, ${colors.secondary}40 0%, transparent 50%),
    radial-gradient(circle at 70% 50%, ${colors.primary}40 0%, transparent 50%)
  `,
  filter: 'blur(60px)',
  transform: 'translateZ(0)',
  zIndex: 0,
});

// Animation variants for framer-motion
export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1,
    },
  },
};

export const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};
